import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from '../lib/routing';
import { authClient } from '../lib/auth-client';
import { exchangeRecoveryToken } from '../lib/session';
import { humanizeAuthError } from '../lib/humanize';
import { isStrongPassword, PASSWORD_TOO_SHORT_MESSAGE } from '../lib/password';
import PasswordChecklist from '../components/password-checklist';
import Header from '../components/header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/* Landing page for the password-recovery email link. supabase-js */
/* (detectSessionInUrl) exchanges the token in the URL for a recovery session */
/* and emits PASSWORD_RECOVERY; we then let the user set a new password. */

export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [failure, setFailure] = useState<'expired' | 'unavailable'>('expired');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;

    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const opaqueToken = params.get('token');

    if (opaqueToken) {
      history.replaceState(null, '', window.location.pathname);
      (async () => {
        /* INVARIANT : `checking` doit retomber quoi qu'il arrive. Un rejet ici
           (panne réseau, réponse illisible) laisserait un utilisateur qui tente
           de récupérer son compte sur « Vérification du lien… », définitivement
           et sans message. Le repli distingue la panne du jeton refusé : les
           deux demandent des gestes opposés. */
        try {
          if (await exchangeRecoveryToken(opaqueToken)) {
            if (!mounted) return;
            setReady(true);
          } else if (mounted) {
            setFailure('expired');
          }
        } catch {
          if (mounted) setFailure('unavailable');
        } finally {
          if (mounted) setChecking(false);
        }
      })();
      return () => { mounted = false; };
    }

    const { data: sub } = authClient.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || session) {
        setReady(true);
        setChecking(false);
      }
    });
    (async () => {
      /* Même invariant : l'échec vaut « pas de session de récupération », et
         l'abonnement ci-dessus reste le chemin de rattrapage. */
      try {
        const { data } = await authClient.getSession();
        if (mounted && data.session) setReady(true);
      } catch { /* ignore */ }
      finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isStrongPassword(password)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE);
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      const { error: authErr } = await authClient.updatePassword(password);
      if (authErr) {
        setError(humanizeAuthError(authErr.message));
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/app'), 1200);
    } catch (e2) {
      setError(humanizeAuthError(e2 instanceof Error ? e2.message : String(e2)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
        <div className="bg-card border border-border rounded-card p-8 shadow-card">
          <h1 className="mt-0 text-[22px] text-foreground">Nouveau mot de passe</h1>

          {checking ? (
            <p className="text-muted-foreground">Vérification du lien…</p>
          ) : !ready ? (
            <Alert className="mt-2 mb-4">
              {failure === 'unavailable'
                ? 'Vérification impossible pour le moment : votre lien reste valable. Vérifiez votre connexion, puis rechargez la page.'
                : 'Lien invalide ou expiré. Redemande un email de réinitialisation depuis la page de connexion.'}
            </Alert>
          ) : done ? (
            <Alert >Mot de passe mis à jour ✅ Redirection…</Alert>
          ) : (
            <form onSubmit={submit} noValidate>
              <Field label="Nouveau mot de passe">
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="10 car. : maj, min, chiffre, symbole"
                    disabled={busy}
                    className="bg-card pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    title={showPw ? 'Masquer' : 'Afficher'}
                    className="absolute right-[2px] top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-transparent border-none p-0 cursor-pointer text-[18px] leading-none"
                  >
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
                {password.length > 0 && <PasswordChecklist password={password} />}
              </Field>
              <Field label="Confirme le mot de passe">
                <Input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="retape-le"
                  disabled={busy}
                  className="bg-card"
                />
              </Field>

              {error && <Alert className="mt-2 mb-4">{error}</Alert>}

              <Button type="submit"  disabled={busy} className="w-full mt-2 font-black text-[18px] shadow-card">
                {busy ? 'Patience…' : 'Mettre à jour'}
              </Button>
            </form>
          )}
        </div>
      </main>
    </>
  );
}


function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block mb-2 font-bold text-foreground text-sm">
        {label}
      </label>
      {children}
    </div>
  );
}
